import { Fragment, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { motion } from 'framer-motion';
import { Blurhash } from 'react-blurhash';
import axios from 'axios';
import Image from 'next/image';
import QRCode from 'react-qr-code';
import { useAppProvider } from '../../context/app';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import {
  declOfNum,
  getPriceDiff,
  getTimeInterval,
  phoneToString,
  removeEmojis,
  sleep,
  toMoney
} from '../../helpers';
import Button from '../Button';
import styles from '../../styles/modules/objects/ObjectsCatalogItem.module.scss';

const getAddress = (a) => {
  const arr = [];

  if (a['locality-name'] && !a) arr.push(a['locality-name']);
  if (a.street) arr.push(a.street);
  if (a.house) arr.push(a.house);

  return arr.join(', ');
};

const ObjectsCatalogItem = ({ item }) => {
  const { width } = useWindowDimensions();
  const [appState, setAppState] = useAppProvider();
  const [ownerPhone, setOwnerPhone] = useState();
  const [slidePosition, setSlidePosition] = useState(1);
  const [loadedImages, setLoadedImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const galleryMaxLength = useRef(Math.floor(item.thumbs.length / 2)).current;
  const photosEl = useRef();

  const handlePhoneShowClick = async () => {
    if (ownerPhone) {
      const a = document.createElement('a');
      a.href = 'tel:+' + ownerPhone;

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      return;
    }

    try {
      setIsLoading(true);
      await sleep(300);

      const response = await axios.get(
        process.env.NEXT_PUBLIC_API + `/contact/${item['sales-agent'].callId}`
      );

      if (!response.data || response.data.status !== 'ok') throw new Error(response.data.text);

      setIsLoading(false);
      setOwnerPhone(response.data.data.phone);
    } catch (error) {}
  };
  const handleItemClick = (e) => {
    if (
      e.target.closest('.ignore') ||
      e.target.closest(`.${styles.itemFavorites}`) ||
      e.target.closest(`.${styles.itemActionsFavorites}`)
    )
      e.preventDefault();
  };
  const handleFavoritesClick = () => {
    let newFavorites;

    if (appState.favorites.includes(item['ads-number'].toString())) {
      newFavorites = appState.favorites.filter((x) => x !== item['ads-number'].toString());
    } else {
      newFavorites = [item['ads-number'].toString(), ...appState.favorites];
    }

    setAppState({
      type: 'SET_APP',
      payload: {
        ...appState,
        favorites: newFavorites
      }
    });
    localStorage.setItem('favorites', JSON.stringify(newFavorites));
  };
  const getItemLabels = () => {
    const customLabels = [];

    for (let i = 0; i < item.labels.length; i++) {
      if (item.labels[i].text === 'Комнат') {
        customLabels.push(
          <div className={styles.itemProperty} key={i}>
            Комнат — {item.labels[i].value}
            {item.labels[i].value === 'Студия' &&
              declOfNum(item.labels[i].value, [' комната', ' комнаты', ' комнат'])}
          </div>
        );
      } else if (item.labels[i].text === 'Площадь' || item.labels[i].text === 'Участок') {
        customLabels.push(
          <div className={styles.itemProperty} key={i}>
            Площадь — {item.labels[i].value} {item.labels[i].unit}
          </div>
        );
      } else if (item.labels[i].text === 'Этаж') {
        customLabels.push(
          <div className={styles.itemProperty} key={i}>
            Этаж — {item.labels[i].value}
          </div>
        );
      }
    }

    return customLabels;
  };

  return (
    <a
      className={styles.item}
      style={{ opacity: item.isActual === 1 ? 1 : 0.5 }}
      href={`/object/${item['ads-number']}`}
      target="_blank"
      onClick={handleItemClick}
      rel="noreferrer"
    >
      <div className={styles.itemGallery}>
        {item.isActual === 1 && (
          <>
            <motion.div
              animate={{ opacity: slidePosition > galleryMaxLength ? 0 : 1 }}
              className={styles.itemGalleryCounter}
            >
              {slidePosition} из {item.thumbs.length}
            </motion.div>
            <div className={styles.itemGalleryButtons}>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: slidePosition > 1 ? 1 : 0 }}
                className={[styles.itemGalleryButtonLeft, slidePosition > 1 && 'ignore']
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => {
                  const photosWidth = photosEl.current.offsetWidth;
                  photosEl.current.scrollTo({
                    left: photosWidth * (slidePosition - 1) - photosWidth,
                    behavior: 'smooth'
                  });
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </motion.div>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: slidePosition <= galleryMaxLength ? 1 : 0 }}
                className={[
                  styles.itemGalleryButtonRight,
                  slidePosition <= galleryMaxLength && 'ignore'
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => {
                  const photosWidth = photosEl.current.offsetWidth;
                  photosEl.current.scrollTo({
                    left: photosWidth * (slidePosition + 1) - photosWidth,
                    behavior: 'smooth'
                  });
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </motion.div>
            </div>
          </>
        )}
        <div
          className={styles.itemGalleryPhotos}
          onScroll={(e) => {
            const selectedSlide = Math.floor(e.target.scrollLeft / e.target.offsetWidth) + 1;
            if (selectedSlide === slidePosition) return;

            setSlidePosition(selectedSlide);
          }}
          ref={photosEl}
        >
          {item.thumbs.slice(0, galleryMaxLength).map((v, i) => (
            <div className={styles.itemGalleryPhoto} key={i}>
              {!!v.blurhash && (
                <Blurhash
                  hash={v.blurhash}
                  width="100%"
                  height="100%"
                  className={styles.itemGalleryPhotoHash}
                  style={{ opacity: loadedImages.includes(v.value) ? 0 : 1 }}
                />
              )}
              {!!v.value && (
                <Image
                  src={v.value}
                  alt=""
                  fill
                  priority={i === 0}
                  style={{ objectFit: 'cover' }}
                  onLoad={() => setLoadedImages((x) => [...x, v.value])}
                />
              )}
            </div>
          ))}
          <div className={[styles.itemGalleryPhoto, styles.itemGalleryPhotoMore].join(' ')}>
            <div className={styles.itemGalleryPhotoMoreText}>
              +{item.thumbs.length - galleryMaxLength} фото
              <span>Нажмите для просмотра</span>
            </div>
          </div>
          <div style={{ width: 8, flexShrink: 0, display: isMobile ? 'block' : 'none' }} />
        </div>
      </div>
      <div className={styles.itemLabels}>
        {!!item.object.exclusive && (
          <div className={[styles.itemLabelsItem, styles.red].join(' ')}>Только в ЛЕНДЛОРД</div>
        )}
        {!!item.price.hersonCert && (
          <div className={[styles.itemLabelsItem, styles.blue].join(' ')}>
            Херсонский сертификат
          </div>
        )}
        {!!item.price.noCommission && (
          <div className={[styles.itemLabelsItem, styles.yellow].join(' ')}>Без комиссии</div>
        )}
      </div>
      <div className={styles.itemData}>
        <div className={styles.itemPrice}>
          {toMoney(item.price.value)} {item.price.currency}
          {getPriceDiff(item.type, item.price) && getPriceDiff(item.type, item.price).icon}
        </div>
        <div className={styles.itemAddress}>{getAddress(item.location)}</div>
        <div className={styles.itemProperties}>{getItemLabels()}</div>
        <div className={styles.itemDescription}>
          {removeEmojis(item.description.slice(0, 450))}
          {removeEmojis(item.description.length > 450 && '...')}
        </div>
        <div className={styles.itemActions}>
          {item['sales-agent'].name && (
            <div className={styles.itemAgent}>
              {item['sales-agent'].avatar && (
                <img
                  src={item['sales-agent'].avatar}
                  alt={item['sales-agent'].name}
                  className={styles.itemAgentAvatar}
                />
              )}
              <span className={styles.itemAgentName}>{item['sales-agent'].name}</span>
            </div>
          )}
          <div className={styles.itemDate}>
            {item.interval > 0 && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ marginRight: 8 }}
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            )}
            {item.interval > 0 && getTimeInterval(item.interval)}
          </div>
          {item.isActual === 1 ? (
            <Fragment>
              {ownerPhone && !isMobile && width > 768 && (
                <QRCode className={styles.qr} value={`tel:${ownerPhone}`} size={46} />
              )}
              <Button
                className="ignore"
                onClick={handlePhoneShowClick}
                loading={isLoading}
                color="green"
                fullWidth={width < 768}
              >
                {ownerPhone ? phoneToString(ownerPhone) : 'Показать телефон'}
              </Button>
            </Fragment>
          ) : (
            <Button
              type="external"
              href={`/object/${item['ads-number']}`}
              color="blue"
              fullWidth={width < 768}
            >
              Посмотреть объект
            </Button>
          )}
          <button
            className={[
              styles.itemActionsFavorites,
              appState.favorites.includes(item['ads-number'].toString()) && styles.active
            ]
              .filter(Boolean)
              .join(' ')}
            type="button"
            onClick={handleFavoritesClick}
            title={
              appState.favorites.includes(item['ads-number'].toString())
                ? 'Удалить из избранного'
                : 'Добавить в избранное'
            }
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="white"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
          </button>
        </div>
      </div>
      <button
        className={[
          styles.itemFavorites,
          appState.favorites.includes(item['ads-number'].toString()) && styles.active
        ]
          .filter(Boolean)
          .join(' ')}
        type="button"
        onClick={handleFavoritesClick}
        title={
          appState.favorites.includes(item['ads-number'].toString())
            ? 'Удалить из избранного'
            : 'Добавить в избранное'
        }
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="white"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
        </svg>
      </button>
    </a>
  );
};

export default ObjectsCatalogItem;
